import { 
  MdOutlineChair,
  MdOutlineMeetingRoom,
  MdOutlineSquareFoot,
  MdOutlineDoorBack,
} from "react-icons/md";
import { PiDeskLight } from "react-icons/pi";
import { FormData, SetFormData } from './FormData.tsx';
import { useEffect } from "react";
import { FaUsers } from "react-icons/fa";

interface listingForm {
  formData: FormData;
  setFormData: SetFormData;
  setNextDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function WorkspaceSpaceSizeRoom({ formData, setFormData, setNextDisabled }: listingForm) {
  const handleInputChange = (field: keyof FormData, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if(!formData.isFurnished) {
      setFormData(prev => ({...prev, desks: 0, chairs: 0 }));
    }
  };

  useEffect(() => {
      if (formData.sizeSqm > 0 && formData.rooms >= 0 && formData.meetingRooms >= 0 && !formData.isFurnished) {
        setNextDisabled(false);
      } else if (formData.sizeSqm > 0 && formData.rooms >= 0 && formData.meetingRooms >= 0 && formData.isFurnished && formData.desks > 0 && formData.chairs > 0) {
        setNextDisabled(false);
      } else {
        setNextDisabled(true);
      }
    }, [formData.sizeSqm, formData.rooms, formData.meetingRooms, formData.isFurnished, formData.desks, formData.chairs]);

  return (
    <div className="space-y-4">
      <h2 className="text-3xl font-bold mb-4">What are some general information about your workspace?</h2>
      <hr className="border-t border-gray-500 my-4 w-full shadow-gray-400" />
      <div className='flex flex-col sm:flex-row sm:flex-wrap gap-4'></div>
      {/* Capacity Input */}
      <div className="p-4 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-3 mb-3">
          <FaUsers className="h-6 w-6 text-gray-500" />
          <h3 className="text-lg font-medium">Capacity</h3>
        </div>
        <input
          type="number"
          value={formData.capacity || ''}
          onChange={(e) => handleInputChange('capacity', Number(e.target.value))}
          className="w-full p-2 border rounded"
          min="0"
          placeholder="Enter maximum capacity"
        />
      </div>
      {/* Size Input */}
      <div className="p-4 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-3 mb-3">
          <MdOutlineSquareFoot className="h-6 w-6 text-gray-500" />
          <h3 className="text-lg font-medium">Size (m²)</h3>
        </div>
        <input
          type="number"
          value={formData.sizeSqm || ''}
          onChange={(e) => handleInputChange('sizeSqm', Number(e.target.value))}
          className="w-full p-2 border rounded"
          min="0"
          placeholder="Enter total area"
        />
      </div>

      {/* Rooms Input */}
      <div className="p-4 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-3 mb-3">
          <MdOutlineDoorBack className="h-6 w-6 text-gray-500" />
          <h3 className="text-lg font-medium">Number of Rooms</h3>
        </div>
        <input
          type="number"
          value={formData.rooms || ''}
          onChange={(e) => handleInputChange('rooms', Number(e.target.value))}
          className="w-full p-2 border rounded"
          min="0"
          placeholder="Enter number of rooms: e.g. offices, conference rooms or lounges - default is 0"
        />
      </div>

      {/* Meeting Rooms Input */}
      <div className="p-4 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-3 mb-3">
          <MdOutlineMeetingRoom className="h-6 w-6 text-gray-500" />
          <h3 className="text-lg font-medium">Number of Meeting Rooms</h3>
        </div>
        <input
          type="number"
          value={formData.meetingRooms || ''}
          onChange={(e) => handleInputChange('meetingRooms', Number(e.target.value))}
          className="w-full p-2 border rounded"
          min="0"
          placeholder="Enter number of meeting rooms: e.g. boardrooms, conference rooms - default is 0"
        />
      </div>

      {/* Furnished Checkbox */}
      <div className="p-4 border border-gray-200 rounded-lg">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isFurnished || false}
            onChange={(e) => handleInputChange('isFurnished', e.target.checked)}
            className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-lg font-medium">This workspace is furnished</span>
        </label>
      </div>

      {/* Conditionally show desks and chairs if furnished */}
      {formData.isFurnished && (
        <>
          {/* Desks Input */}
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <PiDeskLight className="h-6 w-6 text-gray-500" />
              <h3 className="text-lg font-medium">Number of Desks</h3>
            </div>
            <input
              type="number"
              value={formData.desks || ''}
              onChange={(e) => handleInputChange('desks', Number(e.target.value))}
              className="w-full p-2 border rounded"
              min="0"
              placeholder="Enter number of desks"
            />
          </div>

          {/* Chairs Input */}
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <MdOutlineChair className="h-6 w-6 text-gray-500" />
              <h3 className="text-lg font-medium">Number of Chairs</h3>
            </div>
            <input
              type="number"
              value={formData.chairs || ''}
              onChange={(e) => handleInputChange('chairs', Number(e.target.value))}
              className="w-full p-2 border rounded"
              min="0"
              placeholder="Enter number of chairs"
            />
          </div>
        </>
      )}
    </div>
  );
}